import '../../styles.scss';

import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Alert, Box, Button, Flex, Text, Title } from '@gjensidige/builders-components';

import { postEquinorDeath } from '../../api';
import { Input, InputGroup } from '../../components';
import {
  deceasedContactRelationOptions,
  deceasedMaritalStatusOptions,
  deceasedOptions,
  deceasedRelationOptions,
  yesOrNo,
} from '../../constants';
import { Link } from '../../utils/link';
import { EquinorDeath, equinorDeathForm } from '../../utils/types';
import HeaderEquinorForms from './components/headerEquinorForms/HeaderEquinorForms';

const EquinorDeathForm = () => {
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<EquinorDeath>({
    resolver: zodResolver(equinorDeathForm),
  });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [errorSubmitting, setErrorSubmitting] = useState<boolean>(false);

  const onSubmit: SubmitHandler<EquinorDeath> = async (data) => {
    setIsSubmitting(true);
    setErrorSubmitting(false);
    postEquinorDeath(data)
      .then((response) => {
        if (response === 'sent') {
          setIsSubmitting(false);
          setSuccess(true);
        }
      })
      .catch(() => {
        setIsSubmitting(false);
        setErrorSubmitting(true);
      });
  };
  return (
    <form
      method="post"
      onSubmit={(e) => {
        e.preventDefault();
        setIsSubmitting(true);
        handleSubmit(onSubmit);
      }}
    >
      <Flex layout={{ xs: 1, md: 4.8 }} className="schema">
        <div>
          <HeaderEquinorForms
            title="Death"
            description="To fillout form regarding accident/illness"
            url={Link.getEquinorAccidentIllness()}
          />
        </div>
        <div>
          <Box variant="form-section" className="form-section">
            <Title as="h2" size="4">
              Information about the deceased
            </Title>
            <Text className="builders-margin-bottom-60">
              (to be filled in to the best of your knowledge)
            </Text>
            <InputGroup
              name="f01_deceased"
              control={control}
              labelText="About the deceased *"
              options={deceasedOptions}
              type="radio"
              variant="column"
              error={errors.f01_deceased}
              tracking={false}
            />
            <Input
              name="f02_name"
              control={control}
              labelText="Full name *"
              error={errors.f02_name}
              tracking={false}
            />
            <Input
              name="f03_dateOfBirth"
              control={control}
              labelText="Date of birth *"
              placeholder="DD/MM/YYYY"
              error={errors.f03_dateOfBirth}
              tracking={false}
            />
            <Input
              name="f04_address"
              control={control}
              labelText="Address *"
              error={errors.f04_address}
              tracking={false}
            />
            <Input
              name="f05_postalCode"
              control={control}
              labelText="Post code *"
              error={errors.f05_postalCode}
              tracking={false}
            />
            <Input
              name="f06_city"
              control={control}
              labelText="City *"
              error={errors.f06_city}
              tracking={false}
            />
            <Input
              name="f07_position"
              control={control}
              labelText="Position/profession *"
              error={errors.f07_position}
              tracking={false}
            />
            <Input
              name="f08_dateDeath"
              control={control}
              labelText="Date of death *"
              placeholder="DD/MM/YYYY"
              error={errors.f08_dateDeath}
              tracking={false}
            />
            <Input
              name="f09_dateEmployment"
              control={control}
              labelText="Date of employment *"
              placeholder="DD/MM/YYYY"
              error={errors.f09_dateEmployment}
              tracking={false}
            />
            <InputGroup
              name="f10_occurDuringWorkingHours"
              control={control}
              labelText="Did the death occur during working hours? *"
              options={yesOrNo}
              type="radio"
              variant="column"
              error={errors.f10_occurDuringWorkingHours}
              tracking={false}
            />
            <InputGroup
              name="f11_relationToEquinor"
              control={control}
              labelText="The employee’s relation to Equinor *"
              options={deceasedRelationOptions}
              type="radio"
              variant="column"
              error={errors.f11_relationToEquinor}
              tracking={false}
            />
            <InputGroup
              name="f12_maritalStatus"
              control={control}
              labelText="The marital status of the employee *"
              options={deceasedMaritalStatusOptions}
              type="radio"
              variant="column"
              error={errors.f12_maritalStatus}
              tracking={false}
            />
            <InputGroup
              name="f13_childrenUnder21"
              control={control}
              labelText="Did the employee have children under the age of 21 years?"
              options={yesOrNo}
              type="radio"
              variant="column"
              error={errors.f13_childrenUnder21}
              tracking={false}
            />
          </Box>
          <Box variant="form-section">
            <Title as="h3" size="4" className="builders-margin-bottom-40">
              Who can Gjensidige contact?
            </Title>
            <Input
              name="f14_contactName"
              control={control}
              labelText="Name *"
              error={errors.f14_contactName}
              tracking={false}
            />
            <Input
              name="f15_contactDateBirth"
              control={control}
              labelText="Date of birth *"
              placeholder="DD/MM/YYYY"
              error={errors.f15_contactDateBirth}
              tracking={false}
            />
            <Input
              name="f16_contactAddress"
              control={control}
              labelText="Address *"
              error={errors.f16_contactAddress}
              tracking={false}
            />
            <Input
              name="f17_contactPostalCode"
              control={control}
              labelText="Post code *"
              error={errors.f17_contactPostalCode}
              tracking={false}
            />
            <Input
              name="f18_contactCity"
              control={control}
              labelText="City *"
              error={errors.f18_contactCity}
              tracking={false}
            />
            <Input
              name="f19_contactEmail"
              control={control}
              labelText="Email *"
              error={errors.f19_contactEmail}
              tracking={false}
            />
            <Input
              name="f20_contactPhone"
              control={control}
              labelText="Phone number"
              error={errors.f20_contactPhone}
              tracking={false}
            />
            <InputGroup
              name="f21_contactRelation"
              control={control}
              labelText="The contact person’s relation to the deceased *"
              options={deceasedContactRelationOptions}
              type="radio"
              variant="column"
              error={errors.f21_contactRelation}
              tracking={false}
            />
          </Box>
          <Button
            variant="critical"
            type="submit"
            className={'submit-button'}
            loading={isSubmitting}
            onClick={handleSubmit(onSubmit)}
          >
            Submit
          </Button>
          {success && (
            <Alert severity="success" className="sending-alert">
              <Text>Form sent</Text>
            </Alert>
          )}
          {errorSubmitting && (
            <Alert severity="error" className="sending-alert">
              <Text>Failed to send form, try again later</Text>
            </Alert>
          )}
        </div>
      </Flex>
    </form>
  );
};

export default EquinorDeathForm;
